import * as Sentry from '@sentry/nextjs';
import {
  detectPastedCountry,
  UK_MOBILE_LENGTH_ERROR,
  type PhoneValue,
} from '@/components/auth/phone-countries';

/**
 * Tell us when a phone field turns somebody away, so the rule can be judged by
 * what real people type rather than by what we guessed they would.
 *
 * Added with the UK-mobile length check (September 2026). That check refuses
 * anything starting 7 that is not ten national digits, and the question it
 * leaves open is whether it ever refuses a number that was fine. These events
 * answer it: a rejection whose SHAPE looks like a real mobile (ten digits,
 * starting 7) means the rule is wrong, not the person.
 *
 * 🚨 The number itself is never sent. Sentry is an error tracker, not a place
 * for a customer's mobile, and the shape is enough to tell a typo from a bug:
 * how many digits, what it started with, and how it was typed.
 *
 * `Sentry.captureMessage`, never `console.error`: this runs in the browser,
 * where `console.error` reaches nobody (CLAUDE.md #13). Constant message and a
 * fingerprint per reason, so each kind of rejection is ONE issue whose count is
 * how many people hit it; the surface is a tag, so one issue can be split by
 * where it happened.
 */

export type PhoneSurface = 'signup' | 'onboarding_whatsapp' | 'welcome_whatsapp' | 'signup_api';

export type PhoneRejectionReason = 'uk_mobile_length' | 'too_short' | 'too_long' | 'other';

export function reasonFor(error: string): PhoneRejectionReason {
  if (error === UK_MOBILE_LENGTH_ERROR) return 'uk_mobile_length';
  if (/too short/i.test(error)) return 'too_short';
  if (/too long/i.test(error)) return 'too_long';
  return 'other';
}

/** What the number looked like, without the number. */
export function phoneShape(value: PhoneValue) {
  const raw = value.local.trim();
  const pasted = detectPastedCountry(raw);
  const allDigits = raw.replace(/\D/g, '');
  const national = (pasted ? pasted.subscriber : allDigits).replace(/^0+/, '');
  return {
    country: pasted ? pasted.country : value.country,
    typed_as: raw.startsWith('+') ? 'plus' : allDigits.startsWith('00') ? 'double_zero' : allDigits.startsWith('0') ? 'leading_zero' : 'bare',
    national_digits: national.length,
    starts_with: national.slice(0, 1),
    // 44 typed with no plus: people who read "+44" on the chip and type it anyway.
    starts_44_no_plus: !raw.startsWith('+') && allDigits.startsWith('44'),
  };
}

export function reportRejectedPhone(value: PhoneValue, error: string, surface: PhoneSurface) {
  const reason = reasonFor(error);
  const shape = phoneShape(value);
  // An empty field is someone who has not typed yet, not a rule misfiring.
  if (shape.national_digits === 0) return;
  console.warn(`[phone] rejected (${reason}) on ${surface}`, shape);
  Sentry.captureMessage(`Phone number rejected: ${reason}`, {
    level: 'info',
    tags: { area: 'phone-validation', reason, surface, country: shape.country },
    fingerprint: ['phone-rejected', reason],
    extra: shape,
  });
}
